import { useState, useEffect, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { saveAs } from "file-saver";
import { api, formatDate, assetUrl } from "@/lib/api";
import PublicNavbar from "@/components/feature/PublicNavbar";
import PublicFooter from "@/components/feature/PublicFooter";
import { useBranding } from "@/contexts/BrandingContext";
import PageMeta from "@/components/feature/PageMeta";
import { buildCertificateHtml } from "@/components/feature/CertificatePreview";

interface CertResult {
  valid: boolean;
  data?: {
    certificate_number: string;
    student_name: string;
    student_company: string;
    student_position: string;
    course_name: string;
    course_category: string;
    course_duration: string;
    course_description: string;
    hours: number;
    date: string;
    instructor: string;
    evaluator: string;
    qr_code: string;
  };
}

function SignatureBlock({ sig, role }: { sig: string; role: "Instructor" | "Evaluador" }) {
  const isImg = sig.startsWith("/api/") || sig.startsWith("http") || sig.startsWith("data:");
  const fontClass =
    role === "Instructor"
      ? "font-['Great_Vibes','Dancing_Script',cursive] text-[24px]"
      : "font-['Allura','Satisfy',cursive] text-[26px]";
  return (
    <div className="text-center w-full">
      <div className="w-full max-w-[180px] border-b border-[#38424E] mb-1 mx-auto min-h-[40px] flex items-end justify-center">
        {isImg ? (
          <img src={assetUrl(sig)} alt={`Firma ${role}`} className="h-9 max-w-[140px] object-contain" />
        ) : (
          <span className={`text-[#1f2937] leading-none pb-1 ${fontClass}`}>{sig}</span>
        )}
      </div>
      <div className="text-[9px] text-gray-500 uppercase tracking-wider">{role}</div>
    </div>
  );
}

export default function ValidarCertificadoPage() {
  const branding = useBranding();
  const [searchParams] = useSearchParams();
  const [certNumber, setCertNumber] = useState(searchParams.get("numero") || "");
  const [result, setResult] = useState<CertResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const runSearch = async (number: string) => {
    if (!number.trim()) return;
    setSearching(true);
    setResult(null);
    try {
      const data = await api.validateCertificate(number.trim());
      if (data.valid) {
        setResult({
          valid: true,
          data: {
            certificate_number: data.certificate_number || "",
            student_name: data.student_name || "",
            student_company: data.company || "",
            student_position: "",
            course_name: data.course_name || "",
            course_category: data.course_category || "Capacitación",
            course_duration: data.course_duration || "",
            course_description: "",
            hours: data.hours || 0,
            date: data.issue_date || "",
            instructor: data.instructor_signature || "",
            evaluator: data.evaluator_signature || "",
            qr_code: data.qr_code || "",
          },
        });
      } else {
        setResult({ valid: false });
      }
    } catch {
      setResult({ valid: false });
    }
    setSearching(false);
  };

  useEffect(() => {
    const num = searchParams.get("numero");
    if (num) {
      setCertNumber(num);
      runSearch(num);
    }
  }, [searchParams]);

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    await runSearch(certNumber);
  };

  const handleDownloadPdf = async () => {
    if (!result?.data?.certificate_number) return;
    setDownloadingPdf(true);
    try {
      const blob = await api.downloadPublicCertificatePdf(result.data.certificate_number);
      saveAs(blob, `Certificado_${result.data.certificate_number}.pdf`);
    } catch (err) {
      alert("Error al descargar el PDF. Intente nuevamente.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePrint = () => {
    if (!result?.data) return;
    const cert = result.data;
    const sigForPrint = (sig: string) => (sig.startsWith("/api/") ? assetUrl(sig) : sig);
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      window.print();
      return;
    }
    printWindow.document.write(
      buildCertificateHtml(
        {
          certificate_number: cert.certificate_number,
          student_name: cert.student_name,
          course_name: cert.course_name,
          course_category: cert.course_category,
          course_duration: cert.course_duration,
          hours_completed: cert.hours,
          issue_date: cert.date,
          instructor_signature: sigForPrint(cert.instructor),
          evaluator_signature: sigForPrint(cert.evaluator),
          qr_code: cert.qr_code,
        },
        branding.certificate_logo || branding.logo_dark || branding.logo_icon || "/assets/logo-ksm-certificate.png",
        branding.certificate_stamp || undefined,
        branding.logo_icon || "/assets/logo-ksm-certificate.png",
        Number(branding.certificate_stamp_size || 96) || 96
      )
    );
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)] flex flex-col">
      <PageMeta
        title={`Validar certificado | ${branding.site_name}`}
        description="Verifique la autenticidad de certificados emitidos por KSM Services."
        path="/validar-certificado"
      />
      <PublicNavbar />

      {/* HERO BANNER */}
      <section className="relative pt-[72px] pb-16 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={branding.hero_validate}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#080f1a]/35"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#080f1a] via-[#080f1a]/10 to-[#080f1a]/10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#080f1a]/50 via-[#080f1a]/10 to-[#080f1a]/50"></div>
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto text-center py-12 lg:py-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--color-accent)]/10 border border-[var(--color-border-light)] mb-6">
              <i className="ri-verified-badge-line text-[var(--color-text-accent)] text-xs"></i>
              <span className="text-xs text-[var(--color-text-accent)] font-medium uppercase tracking-wider">Verificación Oficial</span>
            </div>

            <img
              src={branding.logo_full}
              alt="KSM Services"
              className="h-14 sm:h-16 w-auto mx-auto mb-6 drop-shadow-[0_0_16px_rgba(255,255,255,0.3)] brightness-0 invert"
            />

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[var(--color-text-primary)] mb-4 tracking-tight"
                style={{ textShadow: "0 2px 16px rgba(0,0,0,0.6)" }}>
              Validación de
              <span className="text-[var(--color-accent-oil)]"> Certificados</span>
            </h1>
            <p className="text-lg text-[var(--color-text-secondary)] max-w-xl mx-auto"
               style={{ textShadow: "0 1px 8px rgba(0,0,0,0.5)" }}>
              Verifique la autenticidad de cualquier certificado emitido por KSM Training ingresando su número de serie.
            </p>
          </div>
        </div>
      </section>

      {/* SEARCH + RESULT */}
      <section className="flex-1 pb-24 lg:pb-32 -mt-8 relative z-10">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">

            {/* Search Form Card */}
            <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-2xl p-6 sm:p-8 shadow-2xl shadow-black/20">
              <form onSubmit={handleSearch} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                    Número de Certificado
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <i className="ri-award-line text-[var(--color-accent-oil)] text-lg"></i>
                    </div>
                    <input
                      type="text"
                      value={certNumber}
                      onChange={(e) => setCertNumber(e.target.value)}
                      placeholder="Ej: KSM-2026-0001"
                      className="w-full bg-[var(--color-bg-input)] border border-[var(--color-border)] rounded-xl pl-11 pr-4 py-4 text-[var(--color-text-primary)] text-lg font-mono placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-accent-oil)] focus:ring-1 focus:ring-[var(--color-accent-oil)]/30 transition-all duration-300"
                      required
                      autoComplete="off"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={searching}
                  className="w-full bg-[var(--color-accent-oil)] text-[#080f1a] font-bold text-base py-4 rounded-xl hover:bg-[#c2912e] transition-all duration-300 flex items-center justify-center gap-3 shadow-lg shadow-[#D4A03C]/20 cursor-pointer disabled:opacity-60 whitespace-nowrap"
                >
                  {searching ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Verificando autenticidad...
                    </>
                  ) : (
                    <>
                      <i className="ri-shield-check-line text-lg"></i>
                      Verificar Certificado
                    </>
                  )}
                </button>
              </form>

              <p className="text-xs text-[var(--color-text-muted)] text-center mt-4">
                Ingrese el número de certificado tal como aparece en su documento. Ejemplo: KSM-2026-0001
              </p>
            </div>

            {/* RESULT: Valid Certificate */}
            {result && result.valid && result.data && (
              <div className="mt-10 animate-[fadeSlideUp_0.6s_ease-out]">
                {/* Valid Badge */}
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-3 bg-green-500/10 border border-green-500/20 rounded-full px-6 py-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-green-500/15 flex items-center justify-center">
                      <i className="ri-shield-check-line text-green-400 text-lg"></i>
                    </div>
                    <span className="text-green-400 font-bold text-lg">Certificado Válido y Auténtico</span>
                  </div>
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    Este certificado ha sido verificado exitosamente en el sistema oficial de KSM Training.
                  </p>
                </div>

                {/* Certificate Card - Printable (white template) */}
                <div id="certificate-printable" className="relative bg-white p-8 sm:p-10 md:p-12 min-h-[560px] flex flex-col overflow-hidden shadow-2xl shadow-black/20 rounded-sm">
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 pointer-events-none z-0 opacity-[0.04]">
                    <img src={branding.logo_icon || "/assets/logo-ksm-certificate.png"} alt="" className="w-[800px] max-w-none object-contain" />
                  </div>
                  {branding.certificate_stamp ? (
                    <div
                      className="absolute top-6 right-6 flex items-center justify-center"
                      style={{
                        width: Math.min(160, Math.max(48, Number(branding.certificate_stamp_size || 96) || 96)),
                        height: Math.min(160, Math.max(48, Number(branding.certificate_stamp_size || 96) || 96)),
                      }}
                    >
                      <img src={branding.certificate_stamp} alt="Sello" className="w-full h-full object-contain" />
                    </div>
                  ) : null}

                  <div className="text-center relative z-10 flex flex-col flex-1 min-h-0">
                    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Allura&family=Dancing+Script:wght@600&family=Satisfy&family=Cinzel:wght@400;700&display=swap" />
                    <div className="min-h-[86px] flex items-center justify-center mb-1">
                      <img
                        src={branding.certificate_logo || branding.logo_dark || branding.logo_icon || "/assets/logo-ksm-certificate.png"}
                        alt="KSM Services"
                        className="h-[96px] max-w-[420px] object-contain mx-auto"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = "/assets/logo-ksm-certificate.png";
                        }}
                      />
                    </div>
                    <div className="text-xs tracking-[3px] uppercase text-[#38424E] font-semibold">Training Management</div>
                    <div className="w-24 h-0.5 bg-[#D4A03C] mx-auto my-3.5"></div>
                    <div className="text-sm tracking-[2px] uppercase text-[#38424E] font-light mb-3.5">Otorga el Presente Certificado a</div>
                    <div className="text-[2rem] sm:text-[2.15rem] font-bold text-[#38424E] mb-4 leading-tight" style={{ fontFamily: "'Cinzel', 'Playfair Display', serif" }}>
                      {result.data.student_name}
                    </div>
                    {result.data.student_company && (
                      <p className="text-sm text-gray-500 mb-2">{result.data.student_company}</p>
                    )}
                    <div className="text-lg sm:text-xl font-bold text-gray-700 leading-snug">{result.data.course_name}</div>
                    <div className="text-sm text-[#D4A03C] font-semibold tracking-wider">
                      {result.data.course_category}{result.data.course_duration ? ` — ${result.data.course_duration}` : ""}
                    </div>

                    <div className="h-7" />
                    <div className="flex justify-between w-full sm:w-4/5 mx-auto gap-4">
                      <div>
                        <div className="text-xl sm:text-2xl font-bold text-[#38424E]">{result.data.hours}h</div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1.5">Horas</div>
                      </div>
                      <div>
                        <div className="text-xl sm:text-2xl font-bold text-[#38424E]">{formatDate(result.data.date)}</div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1.5">Emisión</div>
                      </div>
                      <div>
                        <div className="text-lg sm:text-xl font-bold text-[#38424E] font-mono">{result.data.certificate_number}</div>
                        <div className="text-[10px] text-gray-500 tracking-wider mt-1.5">No. Certificado</div>
                      </div>
                    </div>

                    <div className="flex-1 min-h-6" />
                    <div className="grid grid-cols-2 gap-6 w-full sm:w-4/5 mx-auto">
                      <SignatureBlock sig={result.data.instructor} role="Instructor" />
                      <SignatureBlock sig={result.data.evaluator} role="Evaluador" />
                    </div>

                    <div className="flex justify-between items-end mt-3.5 pt-1">
                      {result.data.qr_code.startsWith("data:") || result.data.qr_code.startsWith("http") ? (
                        <img src={result.data.qr_code} alt="QR" className="w-12 h-12" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 flex items-center justify-center text-[8px] text-gray-400">QR</div>
                      )}
                      <span className="text-[9px] text-gray-400 font-mono">{result.data.certificate_number}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
                  <button
                    onClick={() => {
                      setCertNumber("");
                      setResult(null);
                    }}
                    className="bg-[var(--color-bg-card)] border border-[var(--color-border)] text-[var(--color-text-primary)] px-8 py-3.5 rounded-xl font-semibold text-sm hover:border-[var(--color-border-light)] transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    Verificar otro certificado
                  </button>
                  <button
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf}
                    className="bg-[var(--color-bg-card)] border border-[var(--color-accent-oil)]/30 text-[var(--color-accent-oil)] px-8 py-3.5 rounded-xl font-semibold text-sm hover:bg-[var(--color-accent-oil)]/10 hover:border-[var(--color-accent-oil)]/50 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {downloadingPdf ? (
                      <><i className="ri-loader-4-line animate-spin mr-2"></i> Generando PDF...</>
                    ) : (
                      <><i className="ri-download-2-line mr-2"></i> Descargar PDF</>
                    )}
                  </button>
                  <button
                    onClick={handlePrint}
                    className="bg-[var(--color-bg-card)] border border-[var(--color-accent-oil)]/30 text-[var(--color-accent-oil)] px-8 py-3.5 rounded-xl font-semibold text-sm hover:bg-[var(--color-accent-oil)]/10 hover:border-[var(--color-accent-oil)]/50 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-printer-line mr-2"></i>
                    Imprimir
                  </button>
                  <Link
                    to="/#cursos"
                    className="bg-[var(--color-accent-oil)] text-[#080f1a] px-8 py-3.5 rounded-xl font-bold text-sm hover:bg-[#c2912e] transition-all whitespace-nowrap flex items-center justify-center gap-2 shadow-lg shadow-[#D4A03C]/10"
                  >
                    <i className="ri-book-open-line"></i>
                    Ver cursos disponibles
                  </Link>
                </div>
              </div>
            )}

            {/* RESULT: Invalid */}
            {result && !result.valid && (
              <div className="mt-10 animate-[fadeSlideUp_0.6s_ease-out]">
                <div className="bg-[var(--color-bg-secondary)] border border-red-500/20 rounded-2xl p-8 sm:p-10 text-center">
                  <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-5">
                    <i className="ri-close-circle-line text-red-400 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-red-400 mb-3">Certificado No Encontrado</h3>
                  <p className="text-[var(--color-text-secondary)] max-w-md mx-auto mb-2">
                    El número <span className="font-mono text-[var(--color-text-primary)] font-semibold">{certNumber.trim().toUpperCase()}</span> no corresponde a un certificado válido emitido por KSM Training.
                  </p>
                  <p className="text-sm text-[var(--color-text-muted)] mb-6">
                    Verifique que el número esté escrito correctamente. Si el problema persiste, contáctenos.
                  </p>
                  <button
                    onClick={() => {
                      setCertNumber("");
                      setResult(null);
                    }}
                    className="bg-[var(--color-bg-card)] border border-[var(--color-border)] text-[var(--color-text-primary)] px-8 py-3 rounded-xl font-semibold text-sm hover:border-[var(--color-border-light)] transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line mr-2"></i>
                    Intentar de nuevo
                  </button>
                </div>
              </div>
            )}

            {/* Info CTA */}
            {!result && (
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-xl p-5 text-center hover:border-[var(--color-accent-oil)]/20 transition-all duration-300">
                  <div className="w-10 h-10 rounded-lg bg-[var(--color-accent-oil)]/10 border border-[var(--color-accent-oil)]/20 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-shield-star-line text-[var(--color-accent-oil)]"></i>
                  </div>
                  <p className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">Certificación Oficial</p>
                  <p className="text-xs text-[var(--color-text-muted)]">Todos nuestros certificados cuentan con validez NOM e ISO 9001.</p>
                </div>
                <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-xl p-5 text-center hover:border-[var(--color-accent-oil)]/20 transition-all duration-300">
                  <div className="w-10 h-10 rounded-lg bg-[var(--color-accent-oil)]/10 border border-[var(--color-accent-oil)]/20 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-qr-scan-line text-[var(--color-accent-oil)]"></i>
                  </div>
                  <p className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">Código QR Único</p>
                  <p className="text-xs text-[var(--color-text-muted)]">Cada certificado incluye un código QR para verificación instantánea.</p>
                </div>
                <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-xl p-5 text-center hover:border-[var(--color-accent-oil)]/20 transition-all duration-300">
                  <div className="w-10 h-10 rounded-lg bg-[var(--color-accent-oil)]/10 border border-[var(--color-accent-oil)]/20 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-global-line text-[var(--color-accent-oil)]"></i>
                  </div>
                  <p className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">Reconocimiento Global</p>
                  <p className="text-xs text-[var(--color-text-muted)]">Certificados con validez internacional en el sector Oil & Gas.</p>
                </div>
              </div>
            )}

          </div>
        </div>
      </section>

      <PublicFooter />

      {/* Print-specific styles */}
      <style>{`
        @keyframes fadeSlideUp {
          from {
            opacity: 0;
            transform: translateY(24px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media print {
          @page {
            size: A4 landscape;
            margin: 0;
          }

          body * {
            visibility: hidden !important;
          }

          #certificate-printable,
          #certificate-printable * {
            visibility: visible !important;
          }

          #certificate-printable {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            margin: 0 !important;
            padding: 40px !important;
            border-radius: 0 !important;
            border: none !important;
            box-shadow: none !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            background: #ffffff !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
}